import React, { createContext, useContext, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Button } from "../design/button";
import { useParticipant } from "../contexts/ParticipantContext";
import { Table, TableBody, TableCell, TableRow } from "../design/table";
import PlaidButton from "../components/PlaidButton";
import { Participant } from "../models/Participant";
import EmptyStateView from "../components/EmptyStateView";
import { Subheading } from "../design/heading";
import { useCreateParticipantDialog } from "./CreateParticipantDialogProvider";

interface Context {
  openDialog: (onSelect?: (participantId: Participant["_id"]) => void) => void;
  closeDialog: () => void;
  isOpen: boolean;
}

const ChooseParticipantDialogProviderContext = createContext<
  Context | undefined
>(undefined);

export const ChooseParticipantDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { participants } = useParticipant();
  const { openDialog: openCreateParticipantDialog } =
    useCreateParticipantDialog();
  const [isOpen, setIsOpen] = useState(false);
  const [onSelectCallback, setOnSelectCallback] = useState<
    ((participantId: Participant["_id"]) => void) | undefined
  >(undefined);

  const openDialog = (
    onSelect?: (participantId: Participant["_id"]) => void,
  ) => {
    setOnSelectCallback(() => onSelect);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setOnSelectCallback(undefined);
  };

  const handleSelect = (participantId: Participant["_id"]) => {
    if (onSelectCallback) {
      onSelectCallback(participantId);
    }
    closeDialog();
  };

  return (
    <ChooseParticipantDialogProviderContext.Provider
      value={{ openDialog, closeDialog, isOpen }}
    >
      {isOpen && (
        <Dialog open={isOpen} onClose={closeDialog}>
          <DialogTitle className="flex items-center justify-between">
            <span>Choose Participant</span>
            <Button onClick={() => openCreateParticipantDialog(handleSelect)}>
              Add
            </Button>
          </DialogTitle>

          <DialogBody>
            {participants.length > 0 ? (
              <Table>
                <TableBody>
                  {participants.map((f) => (
                    <TableRow
                      key={f._id}
                      onClick={() => handleSelect(f._id)}
                      className="cursor-pointer"
                    >
                      <TableCell>
                        <Subheading>{f.name}</Subheading>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <EmptyStateView title="No Financial Accounts Found" />
            )}
          </DialogBody>

          <DialogActions>
            <Button plain onClick={closeDialog}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {children}
    </ChooseParticipantDialogProviderContext.Provider>
  );
};

export const useChooseParticipantDialog = () => {
  const ctx = useContext(ChooseParticipantDialogProviderContext);
  if (!ctx)
    throw new Error(
      "useChooseParticipantDialog must be used inside ChooseParticipantDialogProvider",
    );
  return ctx;
};
